import React, { useEffect, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EventDetailDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { EstadoPill, Opciones, dinero, dineroCod, errorText, fechaHora } from "../workshops/comun";
import EventForm from "./eventForm";
import AsistentesTab from "./asistentesTab";
import ElegiblesTab from "./elegiblesTab";
import EntradaTab from "./entradaTab";
import InvitacionesTab from "./invitacionesTab";
import ResultadoTab from "./resultadoTab";
import ExtrasTab from "./extrasTab";

type Tab = "asistentes" | "elegibles" | "invitaciones" | "extras" | "entrada" | "resultado";

interface Props { campusId: number; id: number; opciones: Opciones; onBack: () => void; notify: (t: string, ok?: boolean) => void; }

/** Detalle de un evento con pestañas Asistentes / Elegibles / Entrada / Resultado. */
const EventDetail: React.FC<Props> = ({ campusId, id, opciones, onBack, notify }) => {
	const http = HttpManager.getInstance();
	const [ev, setEv] = useState<EventDetailDTO | null>(null);
	const [tab, setTab] = useState<Tab>("asistentes");
	const [editar, setEditar] = useState<boolean>(false);
	const cargar = () => http.eventsService.details(campusId, id).then(r => setEv(r.data)).catch(e => notify(errorText(e), false));
	useEffect(() => { cargar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, id]);
	if (!ev) return <div className="wau-caja wau-sec">{S.cargando}</div>;
	const soloLectura = ev.isHistoric || ev.status === "anulado";
	const conExtras = (ev.extras || []).length > 0 || (ev.extraSales || []).length > 0;
	const porEntregar = (ev.extraSales || []).filter(x => !x.deliveredAt).length;
	const tabs: Tab[] = (soloLectura ? ["asistentes", "extras", "entrada", "resultado"] : ["asistentes", "elegibles", "invitaciones", "extras", "entrada", "resultado"])
		.filter(t => t !== "extras" || conExtras) as Tab[];

	return (
		<div>
			<div className="wau-acciones" style={{ justifyContent: "space-between", marginTop: 0, marginBottom: 10 }}>
				<button type="button" className="wau-btn s" onClick={onBack}>‹ {S.volver}</button>
				{ev.isHistoric ? <span className="wau-sec">{S.historicoAviso}</span> : <button type="button" className="wau-btn s" onClick={() => setEditar(true)}>{S.editar}</button>}
			</div>
			<div className="wau-caja">
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
					<div><span className="wau-ceja">{ev.typeLabel}</span><h3 style={{ margin: 0, fontSize: 20 }}>{ev.name}</h3></div>
					<EstadoPill status={ev.status} label={ev.statusLabel} />
				</div>
				<div className="wte-datos">
					<div><span className="wau-lab">{S.fecha}</span>{fechaHora(ev.dateTime || ev.date)}</div>
					<div><span className="wau-lab">{S.lugar}</span>{ev.venue || "—"}</div>
					<div><span className="wau-lab">{S.col.asistentes}</span><span className={"wau-num" + (ev.spotsLeft === 0 ? " wau-oro" : "")}>{ev.counts.total}{ev.capacity ? " / " + ev.capacity : ""}</span>
						{/* counts.guests ya incluye a los niños (kind invitado); sumarlos otra vez los contaba doble */}
						<span className="wau-sec" style={{ fontSize: 12.5 }}> · {ev.counts.graduates} {S.kinds.graduada.toLowerCase()}{ev.counts.graduates === 1 ? "" : "s"} · {ev.counts.guests} {S.kinds.invitado.toLowerCase()}{ev.counts.guests === 1 ? "" : "s"}</span></div>
					<div><span className="wau-lab">{S.paquete}</span><span className="wau-num">{dineroCod(ev.currency, ev.packagePrice)}</span></div>
					<div><span className="wau-lab">{S.invitadoExtra} · {S.nino}</span><span className="wau-num">{dineroCod(ev.currency, ev.extraGuestPrice)} · {dineroCod(ev.currency, ev.childPrice)}</span></div>
					{ev.tableSize ? <div><span className="wau-lab">{S.porMesa}</span><span className="wau-num">{ev.tableSize}</span></div> : null}
					{/* Paquetes en su propio renglón: en una celda angosta se partían a media cifra */}
					{ev.guestTiers?.length ? <div style={{ gridColumn: "1 / -1" }}><span className="wau-lab">{S.paquetes}</span>
						<span className="wau-num" style={{ display: "flex", flexWrap: "wrap", gap: "2px 16px" }}>{ev.guestTiers.map(t => <span key={t.guests} style={{ whiteSpace: "nowrap" }}>{S.paqueteCorto(t.guests, dinero(ev.currency, t.total), t.freeChild)}</span>)}</span></div> : null}
				</div>
				{ev.notes ? <p className="wau-sec" style={{ margin: "10px 0 0 0", fontSize: 13.5 }}>{ev.notes}</p> : null}
			</div>
			<div className="wau-tabs" role="tablist">
				{tabs.map(t => <button key={t} type="button" role="tab" className="wau-tab" aria-selected={tab === t} onClick={() => setTab(t)}>
					{S.tabs[t]}{t === "entrada" && ev.counts.checkedIn ? <small>{ev.counts.checkedIn}</small> : null}{t === "extras" && porEntregar ? <small>{porEntregar}</small> : null}</button>)}
			</div>
			{tab === "asistentes" ? <AsistentesTab campusId={campusId} evento={ev} soloLectura={soloLectura} reload={cargar} notify={notify} /> : null}
			{tab === "elegibles" ? <ElegiblesTab campusId={campusId} evento={ev} opciones={opciones} reload={cargar} notify={notify} /> : null}
			{tab === "invitaciones" ? <InvitacionesTab campusId={campusId} evento={ev} notify={notify} /> : null}
			{tab === "extras" ? <ExtrasTab campusId={campusId} evento={ev} soloLectura={soloLectura} reload={cargar} notify={notify} /> : null}
			{tab === "entrada" ? <EntradaTab campusId={campusId} evento={ev} soloLectura={soloLectura} reload={cargar} notify={notify} /> : null}
			{tab === "resultado" ? <ResultadoTab campusId={campusId} evento={ev} soloLectura={soloLectura} reload={cargar} notify={notify} /> : null}
			{editar ? <EventForm campusId={campusId} evento={ev} onClose={() => setEditar(false)} onSaved={(_x, texto) => { setEditar(false); notify(texto); cargar(); }} /> : null}
		</div>
	);
};

export default EventDetail;
